import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { checkHeicSupport } from '../services/heicSupportService';

interface HeicSupportContextType {
  /** Whether the browser natively supports HEIC rendering. null while detection is in progress. */
  heicSupported: boolean | null;
}

const HeicSupportContext = createContext<HeicSupportContextType | undefined>(undefined);

/**
 * Provider that detects HEIC browser support on mount and exposes the result
 * to all descendants via useHeicSupport().
 *
 * When heicSupported is true, HEIC images can be rendered directly via <img> tags
 * with full HDR / P3 / 10-bit fidelity. When false, client-side conversion
 * (e.g. heic-decode + canvas → JPEG) is required before display.
 */
export const HeicSupportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [heicSupported, setHeicSupported] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Wrap in Promise.resolve for robustness — if checkHeicSupport returns a
    // non-thenable value (e.g. in test environments with incomplete mocks),
    // this prevents a runtime crash.
    Promise.resolve(checkHeicSupport()).then((supported) => {
      if (!cancelled) {
        setHeicSupported(supported ?? false);
      }
    }).catch(() => {
      if (!cancelled) {
        setHeicSupported(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <HeicSupportContext.Provider value={{ heicSupported }}>
      {children}
    </HeicSupportContext.Provider>
  );
};

/**
 * Hook to access HEIC support detection result.
 *
 * Returns { heicSupported: false } when used outside a HeicSupportProvider
 * (safe default — assumes conversion path).
 *
 * @returns { heicSupported: boolean | null } — null while detection is still running
 */
export const useHeicSupport = (): HeicSupportContextType => {
  const context = useContext(HeicSupportContext);
  if (context === undefined) {
    // Outside provider — return safe default (HEIC not supported → conversion path)
    return { heicSupported: false };
  }
  return context;
};
